import { useModal } from "@pulse/hooks/contextHooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    type IconDefinition,
    faBadgeCheck,
    faCircleInfo,
    faCircleXmark,
    faTriangleExclamation,
    faTurtle,
} from "@fortawesome/pro-regular-svg-icons";
import { type ReactNode } from "react";
import { Button } from "frontend-components";

interface BaseInfoModalProps {
    text: string;
    subText?: string;
    customButtons?: ReactNode;
}

interface CustomTypeProps extends BaseInfoModalProps {
    type: "custom";
    color: "success" | "error" | "warning" | "info";
    customIcon: IconDefinition;
}

interface NonCustomTypeProps extends BaseInfoModalProps {
    type: "success" | "error" | "warning" | "info" | "turtle";
    customIcon?: never;
    color?: never;
}

interface WithCustomButtons extends BaseInfoModalProps {
    customButtons: ReactNode;
    onButtonClick?: never;
}

interface WithOnButtonClick extends BaseInfoModalProps {
    customButtons?: never;
    onButtonClick?: () => void;
}

type InfoModalProps =
    | (CustomTypeProps & (WithCustomButtons | WithOnButtonClick))
    | (NonCustomTypeProps & (WithCustomButtons | WithOnButtonClick));

const colors = {
    success: "text-success",
    error: "text-error",
    warning: "text-warning-variant",
    info: "text-info",
    turtle: "text-info",
};

const icons = {
    success: faBadgeCheck,
    warning: faTriangleExclamation,
    error: faCircleXmark,
    info: faCircleInfo,
    turtle: faTurtle,
};

export default function InfoModal({
    text,
    type,
    subText,
    customButtons,
    color,
    onButtonClick,
    customIcon,
}: InfoModalProps) {
    const { clearModal } = useModal();
    return (
        <div className="flex flex-col justify-center items-center space-y-4 w-96 m-auto max-w-full">
            <div className={type === "custom" ? colors[color] : colors[type]}>
                {customIcon ? (
                    <FontAwesomeIcon icon={customIcon} size="4x" />
                ) : (
                    <FontAwesomeIcon icon={icons[type]} size="4x" />
                )}
            </div>
            <p className="text-center">{text}</p>
            {subText && (
                <p
                    className={`${type === "custom" ? colors[color] : colors[type]} text-center text-sm`}
                >
                    {subText}
                </p>
            )}
            <div className="flex justify-center sm:justify-end w-full space-x-2">
                {customButtons ? (
                    customButtons
                ) : (
                    <>
                        <Button type="button" onClick={onButtonClick ? onButtonClick : clearModal}>
                            OK
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
